import { bungie, BungieError } from '../lib/bungie';
import { items, table } from '../lib/manifest';
import { bungieName, CLASSES, type Player } from './hub';

/** Port of the app's ClanInfo and ClanAdmin. */

export interface ClanMember {
  name: string; memberId: string; membershipType: number; online: boolean; lastOnline?: Date; joined?: Date; role: string;
  cls?: string; light?: number; guardianRank?: number; lastPlayed?: Date; emblem?: string; seasonRank?: number; weeklyXp?: number; activity?: string;
  pve?: Record<string, { value: number; text: string }>; pvp?: Record<string, { value: number; text: string }>; owned?: Set<number> | null;
  /** the same player's other platform accounts in the clan */
  also?: { memberId: string; membershipType: number }[];
}
export interface Clan {
  groupId: string; name: string; motto: string; about: string; founded?: Date; memberCount: number;
  level: number; levelInto: number; levelSize: number; weeklyXp: number; banner: Record<string, number>; members: ClanMember[];
}

const CLAN_PROGRESSION = 584850370;
const ROLES: Record<number, string> = { 5: 'Founder', 4: 'Acting founder', 3: 'Admin', 1: 'Beginner' };
export const platform = (t: number) => ({ 1: 'Xbox', 2: 'PlayStation', 3: 'Steam', 5: 'Stadia', 6: 'Epic' } as Record<number, string>)[t] ?? '';

export async function loadClan(p: Player): Promise<Clan | null> {
  const groups = await bungie<any>(`/GroupV2/User/${p.membershipType}/${p.membershipId}/0/1/`);
  const group = groups.results?.[0]?.group;
  if (!group?.groupId) return null;
  const prog = group.clanInfo?.d2ClanProgressions?.[CLAN_PROGRESSION];
  const clan: Clan = {
    groupId: group.groupId, name: group.name ?? '', motto: group.motto ?? '', about: group.about ?? '',
    founded: group.creationDate ? new Date(group.creationDate) : undefined, memberCount: group.memberCount ?? 0,
    level: prog?.level ?? 0, levelInto: prog?.progressToNextLevel ?? 0, levelSize: prog?.nextLevelAt ?? 0, weeklyXp: prog?.weeklyProgress ?? 0,
    banner: group.clanInfo?.clanBannerData ?? {}, members: [],
  };
  // one player can be in the clan with several platform accounts (Steam and Xbox under cross save): shown once, on the
  // account cross save plays on, with the others kept for the admin actions, and with the highest rank of them all
  const byPerson = new Map<string, { member: ClanMember; rank: number; primary: boolean }>();
  for (let page = 1; page <= 3; page++) {
    const r = await bungie<any>(`/GroupV2/${clan.groupId}/Members/?currentpage=${page}`);
    for (const m of r.results ?? []) {
      const u = m.destinyUserInfo;
      if (!u?.membershipId) continue;
      const member: ClanMember = {
        name: bungieName(u) || u.displayName || '?', memberId: u.membershipId, membershipType: u.membershipType, online: !!m.isOnline,
        lastOnline: m.lastOnlineStatusChange > 0 ? new Date(m.lastOnlineStatusChange * 1000) : undefined,
        joined: m.joinDate ? new Date(m.joinDate) : undefined,
        role: ROLES[m.memberType] ?? 'Member',
      };
      const primary = !u.crossSaveOverride || u.crossSaveOverride === u.membershipType;
      const rank: number = m.memberType ?? 2;
      const key: string = m.bungieNetUserInfo?.membershipId || u.membershipId;
      const seen = byPerson.get(key);
      if (!seen) { byPerson.set(key, { member, rank, primary }); continue; }
      const takeNew = primary && !seen.primary;
      const keep = takeNew ? member : seen.member, other = takeNew ? seen.member : member;
      keep.also = [...(keep.also ?? []), ...(other.also ?? []), { memberId: other.memberId, membershipType: other.membershipType }];
      const top = Math.max(seen.rank, rank);
      keep.role = ROLES[top] ?? 'Member';
      keep.online ||= other.online;
      if (other.joined && (!keep.joined || other.joined < keep.joined)) keep.joined = other.joined;
      byPerson.set(key, { member: keep, rank: top, primary: seen.primary || primary });
    }
    if (!r.hasMore) break;
  }
  clan.members = [...byPerson.values()].map((p) => p.member);
  return clan;
}

async function chunked<T>(list: T[], size: number, work: (x: T) => Promise<void>) {
  for (let i = 0; i < list.length; i += size) await Promise.all(list.slice(i, i + size).map((x) => work(x).catch(() => undefined)));
}

/** Each member's last-played character: class, Power, guardian rank, emblem, season rank, weekly clan XP, what they are in now. */
export async function loadClanDetails(clan: Clan, seasonProgression: number): Promise<Clan> {
  const members = clan.members.map((m) => ({ ...m }));
  const playing = new Map<ClanMember, number>();
  await chunked(members, 6, async (m) => {
    const p = await bungie<any>(`/Destiny2/${m.membershipType}/Profile/${m.memberId}/?components=100,200,202,204`);
    m.guardianRank = p.profile?.data?.currentGuardianRank ?? 0;
    m.weeklyXp = 0; m.seasonRank = 0;
    for (const c of Object.values<any>(p.characterProgressions?.data ?? {})) {
      m.weeklyXp += c.progressions?.[CLAN_PROGRESSION]?.weeklyProgress ?? 0;
      if (seasonProgression) m.seasonRank = Math.max(m.seasonRank, c.progressions?.[seasonProgression]?.level ?? 0);
    }
    for (const c of Object.values<any>(p.characterActivities?.data ?? {})) if (c.currentActivityHash) playing.set(m, c.currentActivityHash);
    const last = Object.values<any>(p.characters?.data ?? {}).sort((a, b) => (b.dateLastPlayed ?? '').localeCompare(a.dateLastPlayed ?? ''))[0];
    if (!last) return;
    m.cls = CLASSES[last.classType] ?? '';
    m.light = last.light;
    m.lastPlayed = last.dateLastPlayed ? new Date(last.dateLastPlayed) : undefined;
    m.emblem = last.emblemPath;
  });
  if (playing.size) {
    const acts = await table<any>('DestinyActivityDefinition');
    for (const [m, h] of playing) if (m.online) m.activity = acts[h]?.displayProperties?.name ?? '';
  }
  return { ...clan, members };
}

export interface ClanExtras { weekly: { name: string; earned: boolean }[]; perks: { level: number; name: string; desc: string; icon: string }[] }

/** This week's clan engrams and the banner perks by clan level. Either part is left empty when it cannot be read. */
export async function loadClanExtras(clan: Clan): Promise<ClanExtras> {
  const out: ClanExtras = { weekly: [], perks: [] };
  try {
    const state = await bungie<any>(`/Destiny2/Clan/${clan.groupId}/WeeklyRewardState/`);
    const category = state.rewards?.[0];
    if (category) {
      const ms = await table<any>('DestinyMilestoneDefinition');
      const names = ms[state.milestoneHash]?.rewards?.[category.rewardCategoryHash]?.rewardEntries;
      for (const e of category.entries ?? []) {
        const name = names?.[e.rewardEntryHash]?.displayProperties?.name;
        if (name) out.weekly.push({ name, earned: !!e.earned });
      }
    }
  } catch { /* left empty */ }
  try {
    const [progs, lite] = await Promise.all([table<any>('DestinyProgressionDefinition'), items()]);
    (progs[CLAN_PROGRESSION]?.steps ?? []).forEach((s: any, i: number) => {
      for (const r of s.rewardItems ?? []) {
        const d = lite[r.itemHash];
        if (d?.name) out.perks.push({ level: i + 1, name: d.name, desc: d.desc, icon: d.icon });
      }
    });
  } catch { /* left empty */ }
  return out;
}

/** Each member's all-time account totals (PvE and Crucible). */
export async function loadClanStats(clan: Clan): Promise<Clan> {
  const members = clan.members.map((m) => ({ ...m }));
  const read = (from: any) => Object.fromEntries(Object.entries<any>(from ?? {}).map(([k, v]) => [k, { value: v?.basic?.value ?? 0, text: v?.basic?.displayValue ?? '' }]));
  await chunked(members, 16, async (m) => {
    const r = (await bungie<any>(`/Destiny2/${m.membershipType}/Account/${m.memberId}/Stats/?groups=General`)).mergedAllCharacters?.results;
    m.pve = read(r?.allPvE?.allTime); m.pvp = read(r?.allPvP?.allTime);
  });
  return { ...clan, members };
}

/** What each member has collected (null when hidden). */
export async function loadClanItems(clan: Clan): Promise<Clan> {
  const members = clan.members.map((m) => ({ ...m, owned: null as Set<number> | null }));
  await chunked(members, 8, async (m) => {
    const p = await bungie<any>(`/Destiny2/${m.membershipType}/Profile/${m.memberId}/?components=800`);
    if (!p.profileCollectibles?.data) return;
    const owned = new Set<number>();
    const read = (from: any) => { for (const [h, c] of Object.entries<any>(from ?? {})) if (((c.state ?? 0) & 1) === 0) owned.add(Number(h)); };
    read(p.profileCollectibles.data.collectibles);
    for (const c of Object.values<any>(p.characterCollectibles?.data ?? {})) read(c.collectibles);
    m.owned = owned;
  });
  return { ...clan, members };
}

// ---------------- the clan banner ----------------

export interface BannerLayer { path: string; color: string }

/** The clan banner's four parts, each with the colour the clan chose (laid over one another by the page). */
export async function clanBanner(clan: Clan): Promise<BannerLayer[]> {
  const dict = await bungie<any>('/Destiny2/Clan/ClanBannerDictionary/');
  const part = (n: string) => clan.banner[n] ?? 0;
  const rgb = (c: any) => (c ? `rgb(${c.red}, ${c.green}, ${c.blue})` : '');
  const decal = dict.clanBannerDecals?.[part('decalId')];
  return [
    { path: dict.clanBannerGonfalons?.[part('gonfalonId')], color: rgb(dict.clanBannerGonfalonColors?.[part('gonfalonColorId')]) },
    { path: dict.clanBannerGonfalonDetails?.[part('gonfalonDetailId')], color: rgb(dict.clanBannerGonfalonDetailColors?.[part('gonfalonDetailColorId')]) },
    { path: decal?.backgroundPath, color: rgb(dict.clanBannerDecalSecondaryColors?.[part('decalBackgroundColorId')]) },
    { path: decal?.foregroundPath, color: rgb(dict.clanBannerDecalPrimaryColors?.[part('decalColorId')]) },
  ].filter((l) => l.path && l.color);
}

// ---------------- admin (needs the sign-in, and the clan admin permission on the site's Bungie app) ----------------

export interface ClanPerson { name: string; note: string; when?: Date }
export interface ClanAdminData { pending: ClanPerson[]; invited: ClanPerson[]; banned: ClanPerson[] }

export async function loadClanAdmin(token: string, groupId: string): Promise<ClanAdminData> {
  const list = async (what: string, noteField: string, dateField: string) => {
    const r = await bungie<any>(`/GroupV2/${groupId}/${what}/?currentpage=1`, { token });
    return (r.results ?? []).map((m: any) => ({
      name: bungieName(m.destinyUserInfo) || bungieName(m.bungieNetUserInfo) || m.bungieNetUserInfo?.displayName || 'Unknown player',
      note: m[noteField] ?? '', when: m[dateField] ? new Date(m[dateField]) : undefined,
    }));
  };
  return { pending: await list('Members/Pending', 'requestMessage', 'creationDate'), invited: await list('Members/InvitedIndividuals', 'requestMessage', 'creationDate'), banned: await list('Banned', 'comment', 'dateBanned') };
}

/** All of a member's accounts in the clan, the shown one first: rank changes and kicks go to each. */
const accounts = (m: ClanMember) => [{ memberId: m.memberId, membershipType: m.membershipType }, ...(m.also ?? [])];

export async function setRole(token: string, groupId: string, m: ClanMember, role: number) {
  for (const a of accounts(m)) await bungie(`/GroupV2/${groupId}/Members/${a.membershipType}/${a.memberId}/SetMembershipType/${role}/`, { token, method: 'POST', body: {} });
}
export async function kick(token: string, groupId: string, m: ClanMember) {
  for (const a of accounts(m)) await bungie(`/GroupV2/${groupId}/Members/${a.membershipType}/${a.memberId}/Kick/`, { token, method: 'POST', body: {} });
}
/** Bungie's answer when the site's sign-in lacks the clan permission, put in words a player can act on. */
const NO_CLAN_SCOPE = 'the site\'s Bungie sign-in does not have the "Administrate groups and clans" permission. If that permission was only just switched on, sign out (top right) and sign in again. If it still says this, remove D2 Companion under bungie.net > Settings > Authorized applications and sign in once more.';
export const explainClanError = (e: unknown) => {
  const text = e instanceof Error ? e.message : String(e);
  // Bungie's own answer, word for word, in brackets after the explanation: which call, its status and any permission it named
  const wanted = e instanceof BungieError ? e.data?.RequiredScope : undefined;
  const raw = e instanceof BungieError ? ` (Bungie: ${[e.step, `${e.status} ${e.code}`, wanted && `wants ${wanted}`].filter(Boolean).join(', ')})` : '';
  if (!/application scope/i.test(text)) return text + raw;
  // the clan permission is fixed by signing in again; any other one is not offered to sites
  if (!wanted || wanted === 'AdminGroups') return NO_CLAN_SCOPE + raw;
  return `Bungie keeps this for its own site and apps (it wants the "${wanted}" permission, which Bungie does not offer to other sites). Do it in the game or on bungie.net instead.${raw}`;
};
